/**
 * T115: Rendered Prompt View
 * Display rendered prompt output with copy functionality
 */

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { CopyButton } from './copy-button'
import type { GeneratePromptResponse, PromptContent } from '@/services/prompts'

interface RenderedPromptViewProps {
  result: GeneratePromptResponse | null
}

function getTextContent(content: PromptContent): string {
  if (content.type === 'text') {
    return content.text
  }
  if (content.type === 'chat') {
    return content.messages
      .map((m) => `[${m.role}]\n${m.content}`)
      .join('\n\n')
  }
  if (content.type === 'multipart') {
    return content.parts
      .filter((p) => p.type === 'text' && p.text)
      .map((p) => p.text)
      .join('\n\n')
  }
  return ''
}

export function RenderedPromptView({ result }: RenderedPromptViewProps) {
  if (!result) {
    return (
      <Card>
        <CardContent className="py-6">
          <p className="text-sm text-muted-foreground">
            Fill in the variables and click Generate to see the rendered prompt.
          </p>
        </CardContent>
      </Card>
    )
  }

  const renderedText = getTextContent(result.rendered_content)

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-lg">Rendered Output</CardTitle>
        <CopyButton text={renderedText} variant="outline" size="sm">
          Copy
        </CopyButton>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {result.variables_used.length > 0 && (
            <div className="rounded-md bg-muted p-3">
              <p className="text-sm font-medium mb-2">Variables Used:</p>
              <ul className="text-sm space-y-1">
                {result.variables_used.map((v) => (
                  <li key={v.name}>
                    <span className="font-mono text-primary">{`{{${v.name}}}`}</span>
                    {' → '}
                    <span className="text-muted-foreground">{v.value}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div className="rounded-md border bg-background p-4">
            <pre className="whitespace-pre-wrap text-sm">{renderedText}</pre>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
